import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Rating,
  Typography,  
} from "@mui/material";
import { CalendarMonth, HourglassTop, Launch, LocalAtm, MyLocation, Schedule, Star } from "@mui/icons-material";

import { RootState } from "../../../store/Index";

import { CallsheetProps } from "../tabs/Assignments";
import { useNavigate } from "react-router-dom";

interface ViewAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  src: string | null;
  assignment: CallsheetProps | undefined;
};

interface StreamProps {
  id: number;
  title: string;
  date: string;
  start: string;
  time: string;
  address_one: string | null;
  address_two: string | null;
  suburb: string | null;
  city: string | null;
};

export const ViewAssignmentDialog: React.FC<ViewAssignmentDialogProps> = ({
  open,
  onClose,
  src,
  assignment
}) => {
  const [stream, setStream] = useState<StreamProps | null>();
  const [streamLoading, setStreamLoading] = useState<boolean>(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const navigate = useNavigate();

  useEffect(() => {
    setStreamLoading(true);
    if (assignment) {
      setStream(assignment.stream);
    };
    setStreamLoading(false);
  }, [assignment]);

  const rating = Math.floor(Math.random() * (5 - 1 + 1)) + 1;
  const payout = Math.floor(Math.random() * (5 - 1 + 1)) + 1;

  if (!assignment || !stream) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {streamLoading ? (
          <CircularProgress color={'primary'} size={16} />
        ) : (
          <Box display={'flex'} alignItems={'center'} sx={{ mb: 1 }} gap={2}>
            <Box
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  color: reduxTheme.primary_colors[400]
                }
              }}
              onClick={() => navigate(`/stream-management/view/${assignment.stream.id}`)}
              display={'flex'}
              alignItems={'center'}
              gap={.4}>
                <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{stream?.title}</Typography>
                <Launch sx={{ fontSize: 14 }} />
            </Box>
          </Box>
        )}
      </DialogTitle>
      <DialogContent>
        <Box display={'flex'} alignItems={'center'} sx={{ mb: 1 }} gap={1}>
          <Typography variant={'body1'} sx={{ fontWeight: 600 }}>{assignment.crew_member.full_name}</Typography>
          <Chip
            label={assignment.assignment[0].role}
            size={'small'}
            sx={{
              bgcolor: "transparent",
              border: '1px solid',
              borderColor: "#724E41",
              fontWeight: 600,
              textTransform: "uppercase",
              color: "#724E41",
              borderRadius: 1
            }}
          />
        </Box>
        
        <Typography variant={'body2'}>{assignment.assignment[0].notes}</Typography>
        
        <Box sx={{ mt: 2 }}>
          <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
            <Box display={'flex'} alignItems={'center'} gap={.8}>
              <MyLocation sx={{ fontSize: 18, color: "#724E41" }} />
              <Typography variant={'body2'}>Venue Details</Typography>
            </Box>
            
            <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
              <Typography variant={'body2'} sx={{ fontWeight: 600 }}>
                {`${stream.address_one} ${stream.address_two}`}
              </Typography>
              <Typography variant={'caption'}>
                {`${stream.suburb ? stream.suburb : null}, ${stream.city}`}
              </Typography>
            </Box>
          </Box>

          <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
            <Box display={'flex'} alignItems={'center'} gap={.8}>
              <CalendarMonth sx={{ fontSize: 18, color: "#724E41" }} />
              <Typography variant={'body2'}>Date</Typography>
            </Box>

            <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
              <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{format(new Date(stream.date), "dd MMM, yyyy")}</Typography>
            </Box>
          </Box>

          {src === 'upcoming' && (
            <>
              <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
                <Box display={'flex'} alignItems={'center'} gap={.8}>
                  <HourglassTop sx={{ fontSize: 18, color: "#724E41" }} />
                  <Typography variant={'body2'}>Call Time</Typography>
                </Box>

                <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
                  <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{stream.start}</Typography>
                </Box>
              </Box>

              <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
                <Box display={'flex'} alignItems={'center'} gap={.8}>
                  <Schedule sx={{ fontSize: 18, color: "#724E41" }} />
                  <Typography variant={'body2'}>Time</Typography>
                </Box>

                <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
                  <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{stream.start}</Typography>
                </Box>
              </Box>
            </>
          )}

          {src === 'past' &&
            <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
              <Box display={'flex'} alignItems={'center'} gap={.8}>
                <Star sx={{ fontSize: 18, color: "#724E41" }} />
                <Typography variant={'body2'}>Rating</Typography>
              </Box>

              <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
                <Rating value={rating} precision={0.1} size={'small'} readOnly />
              </Box>
            </Box>
          }

          <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
            <Box display={'flex'} alignItems={'center'} gap={.8}>
              <LocalAtm sx={{ fontSize: 18, color: "#724E41" }} />
              <Typography variant={'body2'}>Budget</Typography>
            </Box>

            <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
              <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{`$${payout * 10}`}</Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewAssignmentDialog;
